/**
 * Large-room grid limits (M4 item 5, the camera-slice handover): rest-grid tile pagination that
 * bounds the subscriber's decode budget at 40 participants.
 *
 * The SDK escape hatch this was specced against - livekit-client's
 * `Room.setTrackSubscriptionLimits` - does not exist in the pinned 2.22.3 (verified against the
 * installed typings: only the server-driven `setTrackSubscriptionPermissions` ships), so this is
 * the mission's sanctioned alternative: visible-tile pagination. Past
 * {@link GRID_MAX_TILES} rest tiles the layout renders one page of tiles at a time; OFF-page
 * camera publications are explicitly unsubscribed (`RemoteTrackPublication.setSubscribed(false)`),
 * because a subscribed track with no attached element would keep decoding - adaptiveStream's
 * visibility pause only reaches elements that exist. On-page tiles keep the subscribeQuality.ts
 * ladder untouched (the pagination decides WHICH tiles subscribe, never at what size), so the
 * DPR ladder and the dominant slot behave exactly as before.
 *
 * Everything here is pure: the layout supplies the ordered rest keys and the requested page and
 * renders the window; tests pin the windowing, clamping and page-count math.
 */

/** Rest tiles rendered per page once the grid grows past this bound. */
export const GRID_MAX_TILES = 12;

/** The pagination window of one grid page. */
export interface GridPage {
  /** 0-based page index, clamped into [0, pageCount-1]. */
  readonly page: number;
  /** Total pages (>= 1, even for an empty key list). */
  readonly pageCount: number;
  /** The keys rendered on the (clamped) page, in input order. */
  readonly keys: readonly string[];
}

/** Page count for `total` rest tiles; an empty or negative total still answers one page. */
export function gridPageCount(total: number): number {
  if (total <= 0) {
    return 1;
  }
  return Math.ceil(total / GRID_MAX_TILES);
}

/** Clamps a requested page into the valid range. */
export function clampGridPage(page: number, pageCount: number): number {
  if (!Number.isFinite(page)) {
    return 0;
  }
  return Math.max(0, Math.min(Math.trunc(page), pageCount - 1));
}

/**
 * The visible window of the rest grid: `allKeys` in render order, the requested page clamped.
 * Below the pagination bound the single page carries every key, so the layout needs no
 * special case beyond consulting this function.
 */
export function gridWindow(allKeys: readonly string[], page: number): GridPage {
  const pageCount = gridPageCount(allKeys.length);
  const safePage = clampGridPage(page, pageCount);
  const start = safePage * GRID_MAX_TILES;
  return {
    page: safePage,
    pageCount,
    keys: allKeys.slice(start, start + GRID_MAX_TILES),
  };
}
